import React, { useState, useEffect, useMemo } from 'react'
import PastBookDetail from './PastBookDetail'
import { formatDate } from '../utils/dateFormat'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface BookPick {
  id: number
  status: string
  started_at?: string
  completed_at?: string
  book: Book
  picked_by?: {
    id: number
    name: string
    email: string
  }
  season?: {
    id: number
    season_number: number
  }
  unclaimed: boolean
  average_rating?: number
  review_count: number
}

interface PastReadsListProps {
  clubId: number
  clubName: string
  onBack: () => void
}

type SortField = 'title' | 'author' | 'genre' | 'season' | 'rating' | 'completed_at' | 'picked_by'
type SortDirection = 'asc' | 'desc'

export default function PastReadsList({ clubId, clubName, onBack }: PastReadsListProps) {
  const [books, setBooks] = useState<BookPick[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [genreFilter, setGenreFilter] = useState<string>('all')
  const [seasonFilter, setSeasonFilter] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('completed_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedBook, setSelectedBook] = useState<BookPick | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [claimingBook, setClaimingBook] = useState<number | null>(null)

  useEffect(() => {
    fetchBooks()
  }, [clubId])

  const fetchBooks = async () => {
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}/book_picks`)
      if (response.ok) {
        const data = await response.json()
        const completed = data.book_picks.filter((pick: BookPick) => pick.status === 'completed')
        setBooks(completed)
      }
    } catch (err) {
      console.error('Failed to fetch books:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClaimBook = async (bookPickId: number) => {
    setClaimingBook(bookPickId)
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/book_picks/${bookPickId}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        // Refresh books to show updated picker
        fetchBooks()
      } else {
        const data = await response.json()
        console.error('Failed to claim book:', data.error)
      }
    } catch (err) {
      console.error('Error claiming book:', err)
    } finally {
      setClaimingBook(null)
    }
  }

  // Get unique genres and seasons for filters
  const genres = useMemo(() => {
    const uniqueGenres = new Set(books.map(b => b.book.genre).filter(Boolean))
    return Array.from(uniqueGenres).sort()
  }, [books])

  const seasons = useMemo(() => {
    const uniqueSeasons = new Set(books.map(b => b.season?.season_number).filter(Boolean))
    return Array.from(uniqueSeasons).sort((a, b) => (a as number) - (b as number))
  }, [books])

  // Calculate stats
  const stats = useMemo(() => {
    const totalBooks = books.length
    const ratingsOnly = books.filter(b => b.average_rating).map(b => b.average_rating!)
    const avgRating = ratingsOnly.length > 0
      ? (ratingsOnly.reduce((sum, r) => sum + r, 0) / ratingsOnly.length).toFixed(1)
      : null

    const genreCounts = books.reduce((acc, book) => {
      if (book.book.genre) {
        acc[book.book.genre] = (acc[book.book.genre] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    const favoriteGenre = Object.entries(genreCounts).sort((a, b) => b[1] - a[1])[0]?.[0]

    return { totalBooks, avgRating, favoriteGenre }
  }, [books])

  // Filter and sort books
  const filteredAndSortedBooks = useMemo(() => {
    let filtered = books

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(book =>
        book.book.title.toLowerCase().includes(query) ||
        book.book.author.toLowerCase().includes(query)
      )
    }

    // Genre filter
    if (genreFilter !== 'all') {
      filtered = filtered.filter(book => book.book.genre === genreFilter)
    }

    // Season filter
    if (seasonFilter !== 'all') {
      filtered = filtered.filter(book => book.season?.season_number === parseInt(seasonFilter))
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal: any, bVal: any

      switch (sortField) {
        case 'title':
          aVal = a.book.title.toLowerCase()
          bVal = b.book.title.toLowerCase()
          break
        case 'author':
          aVal = a.book.author.toLowerCase()
          bVal = b.book.author.toLowerCase()
          break
        case 'genre':
          aVal = a.book.genre?.toLowerCase() || ''
          bVal = b.book.genre?.toLowerCase() || ''
          break
        case 'season':
          aVal = a.season?.season_number || 0
          bVal = b.season?.season_number || 0
          break
        case 'rating':
          aVal = a.average_rating || 0
          bVal = b.average_rating || 0
          break
        case 'completed_at':
          aVal = a.completed_at ? new Date(a.completed_at).getTime() : 0
          bVal = b.completed_at ? new Date(b.completed_at).getTime() : 0
          break
        case 'picked_by':
          aVal = a.picked_by?.name.toLowerCase() || ''
          bVal = b.picked_by?.name.toLowerCase() || ''
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [books, searchQuery, genreFilter, seasonFilter, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Past Reads</h1>
              <p className="text-sm text-gray-600">{clubName}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBooks}</p>
                <p className="text-sm text-gray-600">Books Read</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.avgRating || 'N/A'}</p>
                <p className="text-sm text-gray-600">Average Rating</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-teal-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900 truncate">{stats.favoriteGenre || 'None'}</p>
                <p className="text-sm text-gray-600">Favorite Genre</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Genre Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Genre</label>
              <select
                value={genreFilter}
                onChange={(e) => setGenreFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Genres</option>
                {genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </div>

            {/* Season Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Season</label>
              <select
                value={seasonFilter}
                onChange={(e) => setSeasonFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Seasons</option>
                {seasons.map(season => (
                  <option key={season} value={season}>Season {season}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(searchQuery || genreFilter !== 'all' || seasonFilter !== 'all') && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full hover:bg-indigo-200"
                >
                  Search: "{searchQuery}"
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {genreFilter !== 'all' && (
                <button
                  onClick={() => setGenreFilter('all')}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full hover:bg-indigo-200"
                >
                  Genre: {genreFilter}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {seasonFilter !== 'all' && (
                <button
                  onClick={() => setSeasonFilter('all')}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full hover:bg-indigo-200"
                >
                  Season: {seasonFilter}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Showing {filteredAndSortedBooks.length} of {books.length} books
          </p>
        </div>

        {/* Books Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredAndSortedBooks.length > 0 ? (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Book
                    </th>
                    <th
                      onClick={() => handleSort('genre')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        Genre
                        <SortIcon field="genre" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('season')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        Season
                        <SortIcon field="season" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('rating')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        Rating
                        <SortIcon field="rating" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('picked_by')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        Picked By
                        <SortIcon field="picked_by" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('completed_at')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        Completed
                        <SortIcon field="completed_at" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedBooks.map((book) => (
                    <tr key={book.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {book.book.cover_url ? (
                            <img
                              src={book.book.cover_url}
                              alt={book.book.title}
                              className="w-10 h-14 object-cover rounded shadow-sm"
                            />
                          ) : (
                            <div className="w-10 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900">{book.book.title}</p>
                            <p className="text-sm text-gray-600">by {book.book.author}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {book.book.genre || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {book.season ? `Season ${book.season.season_number}` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        {book.average_rating ? (
                          <div className="flex items-center gap-2">
                            {renderStars(Math.round(book.average_rating))}
                            <span className="text-sm font-medium text-gray-900">{book.average_rating}</span>
                            <span className="text-xs text-gray-500">({book.review_count})</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No reviews</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {book.picked_by?.name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {book.completed_at ? formatDate(book.completed_at) : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedBook(book)
                              setShowDetail(true)
                            }}
                            className="text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                          >
                            View Details
                          </button>
                          {book.unclaimed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClaimBook(book.id)
                              }}
                              disabled={claimingBook === book.id}
                              className="text-indigo-600 hover:text-indigo-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {claimingBook === book.id ? 'Claiming...' : 'Claim'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600">No books found matching your filters</p>
          </div>
        )}
      </div>

      {/* Book Detail Modal */}
      {showDetail && selectedBook && (
        <PastBookDetail
          bookPick={selectedBook}
          clubId={clubId}
          onClose={() => {
            setShowDetail(false)
            setSelectedBook(null)
          }}
          onDelete={() => {
            fetchBooks()
          }}
        />
      )}
    </div>
  )
}
